// Translation layer between the Bell System API DTOs and the dashboard's view
// models. Enum labels, stage ordering and row shaping live here so components
// stay presentation-only (CLAUDE.md: business logic outside UI).

import type {
  AdminDeal,
  AdminDealStatus,
  AdminPipeline,
  AdminAlertItem,
  AdminBellEvent,
} from "./api";
import type { DealRow, DealStatus } from "./data";

// ---- Deal status ----------------------------------------------------------

const STATUS_LABEL: Record<AdminDealStatus, DealStatus> = {
  ACTIVE: "Active",
  PENDING_FOLLOW_UP: "Pending Follow-Up",
  SOLD: "Closed (Sold)",
  LOST: "Closed (Lost)",
};

const STATUS_API: Record<DealStatus, AdminDealStatus> = {
  Active: "ACTIVE",
  "Pending Follow-Up": "PENDING_FOLLOW_UP",
  "Closed (Sold)": "SOLD",
  "Closed (Lost)": "LOST",
};

export function statusLabel(status: AdminDealStatus | string): DealStatus {
  return STATUS_LABEL[status as AdminDealStatus] ?? "Active";
}

export function statusParam(status: DealStatus): AdminDealStatus {
  return STATUS_API[status];
}

// ---- Enum prettifying -----------------------------------------------------

/** UPPER_SNAKE_CASE -> "Upper Snake Case". */
export function humanize(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function personName(p: { firstName?: string; lastName?: string } | null | undefined): string {
  if (!p) return "—";
  return `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || "—";
}

// ---- Deals ----------------------------------------------------------------

/** API deal -> table row. Gross is not part of the deal list contract yet, so
 *  it stays 0 and renders as "—" rather than inventing a number. */
export function toDealRow(d: AdminDeal): DealRow {
  return {
    id: d.code || String(d.id),
    customer: d.customerName || "Guest Customer",
    vehicle: d.vehicle || "—",
    salesperson: personName(d.salesperson),
    manager: personName(d.manager),
    bells: d.bellCount ?? 0,
    status: statusLabel(d.status),
    gross: 0,
    testDrive: d.currentStage === "TEST_DRIVE",
    testDriveAt: d.currentStage === "TEST_DRIVE" ? d.checkInAt ?? undefined : undefined,
  };
}

// ---- Pipeline -------------------------------------------------------------

export interface PipelineRow {
  stage: string;
  count: number;
  color: string;
}

// Display order for the funnel; stages the API omits are dropped.
const STAGE_ORDER = [
  "MEET_AND_GREET",
  "NEEDS_ANALYSIS",
  "VEHICLE_SELECTION",
  "VEHICLE_PRESENTATION",
  "TEST_DRIVE",
  "TRADE_IN_WALK",
  "TRIAL_CLOSE",
  "MANAGER_REQUESTED",
  "MANAGER_ENGAGED",
  "NEGOTIATING",
  "PENDING",
  "SOLD",
  "LOST",
];

/** How many rows a pipeline skeleton should draw. The funnel renders one row
 *  per known stage the API returns, so this is the count the loaded panel
 *  settles at — keeping the placeholder the same height avoids a jump. Stages
 *  the API omits shrink it, and stages outside STAGE_ORDER grow it; both are
 *  rarer than the full set, so the full set is the right guess. */
export const PIPELINE_STAGE_COUNT = STAGE_ORDER.length;

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: "var(--accent)",
  PENDING_FOLLOW_UP: "var(--secondary)",
  SOLD: "var(--ok)",
  LOST: "var(--text-faint)",
};

export function pipelineStageRows(p: AdminPipeline): PipelineRow[] {
  const stages = p.byStage ?? {};
  const known = STAGE_ORDER.filter((s) => s in stages);
  const extra = Object.keys(stages).filter((s) => !STAGE_ORDER.includes(s));
  return [...known, ...extra].map((stage) => ({
    stage: humanize(stage),
    count: stages[stage] ?? 0,
    color: "var(--accent)",
  }));
}

export function pipelineStatusRows(p: AdminPipeline): PipelineRow[] {
  const byStatus = p.byStatus ?? {};
  return Object.entries(byStatus).map(([status, count]) => ({
    stage: statusLabel(status),
    count,
    color: STATUS_COLOR[status] ?? "var(--accent)",
  }));
}

// ---- Operational alerts ---------------------------------------------------

export type AlertSeverity = "critical" | "warning";

export interface AlertRow {
  id: string;
  severity: AlertSeverity;
  type: string;
  message: string;
  count: number;
  timestamp: string;
}

function alertRef(a: AdminAlertItem): string {
  const code = a.dealCode || a.code;
  return code ? `#${code}` : a.dealId ? `#${a.dealId}` : "Deal";
}

function clockTime(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(11, 19); // reporting timezone is UTC
}

/** Flattens the dashboard's alert buckets into one severity-ranked list. */
export function toAlertRows(
  alerts: {
    customerLeaving: AdminAlertItem[];
    highBellCount: { items: AdminAlertItem[]; available: boolean; reason?: string };
  } | undefined,
): AlertRow[] {
  if (!alerts) return [];

  const leaving: AlertRow[] = (alerts.customerLeaving ?? []).map((a, i) => ({
    id: `leaving-${a.dealId ?? a.id ?? i}`,
    severity: "critical",
    type: "Customer Leaving",
    message: `${alertRef(a)} · ${a.customerName ?? "Customer"} — no engagement yet`,
    count: 1,
    timestamp: clockTime(a.updatedAt ?? a.createdAt),
  }));

  const highBell: AlertRow[] = (alerts.highBellCount?.items ?? []).map((a, i) => ({
    id: `bells-${a.dealId ?? a.id ?? i}`,
    severity: "warning",
    type: "High Bell Count",
    message: `${alertRef(a)} hit ${a.bellCount ?? 0} bells — negotiation stalling`,
    count: a.bellCount ?? 1,
    timestamp: clockTime(a.updatedAt ?? a.createdAt),
  }));

  return [...leaving, ...highBell];
}

// ---- Bell events ----------------------------------------------------------

const BELL_TINT: Record<string, { color: string; pastel: string }> = {
  CUSTOMER_ON_LOT: { color: "var(--secondary)", pastel: "var(--lavender)" },
  NEED_MANAGER_HELP: { color: "var(--accent)", pastel: "var(--mint)" },
  MANAGER_ENGAGED: { color: "var(--ok)", pastel: "var(--mint)" },
  COUNTER_OFFER: { color: "var(--warn)", pastel: "var(--butter)" },
  ON_TEST_DRIVE: { color: "var(--secondary)", pastel: "var(--sky)" },
  TEST_DRIVE_ABOUT_TO_ARRIVE: { color: "var(--secondary)", pastel: "var(--sky)" },
  CUSTOMER_LEAVING: { color: "var(--danger)", pastel: "var(--rose)" },
  ESCALATION: { color: "var(--danger)", pastel: "var(--rose)" },
};

export function bellTint(type: string): { color: string; pastel: string } {
  return BELL_TINT[type] ?? { color: "var(--accent)", pastel: "var(--mint)" };
}

/** Response time is reported in seconds; null means "still awaiting". */
export function bellResponse(b: AdminBellEvent): string {
  if (b.responseTime === null || b.responseTime === undefined) return "awaiting";
  const s = Math.round(b.responseTime);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}
